import { Box } from '@uhg-abyss/web/ui/Box';
import { IconBrand, IconBrandProps } from '@uhg-abyss/web/ui/IconBrand';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { t } from 'i18next';
import { find } from 'lodash';
import React from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { Markdown } from '../../../../common/Markdown';
import { useLagoon } from '../../../../hooks/useLagoon';
import { MedicalVisionDrawer } from './MedicalVisionDrawer';

const InfoBox = ({
  color,
  icon,
  title,
  bullets,
  isMobile,
  testId,
}: {
  color: string;
  icon: IconBrandProps['icon'];
  title?: string;
  bullets?: string;
  isMobile: boolean;
  testId: string;
}) => (
  <Box
    color={color}
    css={{
      borderRadius: '8px',
      gap: '4px',
      display: 'flex',
      flexDirection: 'column',
    }}
    data-auto-testid={testId}
    data-testid={testId}
    padding="8px 16px 16px 16px"
  >
    <Layout.Group>
      <IconBrand icon={icon} size={48} variant="twotone" />
      <Text
        color="$core.color.brand.100"
        css={{ justifyContent: 'center' }}
        fontSize={isMobile ? 'sm' : 'md'}
        fontWeight="bold"
      >
        {title}
      </Text>
    </Layout.Group>
    <Text
      color="$core.color.neutral.80"
      css={{ marginLeft: '4px' }}
      fontSize={isMobile ? 'xs' : 'sm'}
      fontWeight="medium"
    >
      <Markdown>{bullets}</Markdown>
    </Text>
  </Box>
);

export const MedicalVisionInfographic = ({
  isMobile = false,
}: {
  isMobile?: boolean;
}) => {
  const uiMessaging = useLagoon('ui-messaging')();
  const medicalPlanMessaging = find(uiMessaging, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .MEDICAL_VS_VISION_INFOGRAPH_MEDICAL_DESCRIPTION,
  });
  const medicalPlanTitle = medicalPlanMessaging?.title;
  const medicalPlanCoversBullets = medicalPlanMessaging?.message;
  const visionPlanMessaging = find(uiMessaging, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .MEDICAL_VS_VISION_INFOGRAPH_VISION_DESCRIPTION,
  });
  const visionPlanTitle = visionPlanMessaging?.title;
  const visionPlanCoversBullets = visionPlanMessaging?.message;

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  return (
    <React.Fragment>
      <Layout.Stack
        alignItems="left"
        alignLayout="right"
        css={{
          width: '100%',
          maxWidth: '486px',
          marginLeft: isMobile ? '0' : 'auto',
          '@media (max-width: 744px)': {
            paddingRight: '12px',
            paddingLeft: '16px',
          },
        }}
        data-auto-testid="medical-vs-vision-infographic"
        data-testid="medical-vs-vision-infographic"
        grow
      >
        <Text
          color={'$core.color.brand.100'}
          fontSize={isMobile ? 'md' : 'lg'}
          fontWeight="bold"
        >
          {t('MEDICAL_SPECIALISTS_VS_VISION.Whats_covered')}
        </Text>
        <Layout.Group
          css={{
            '@media (max-width: 744px)': {
              paddingRight: '16px',
            },
          }}
          grow
          width="50%"
        >
          <InfoBox
            bullets={medicalPlanCoversBullets}
            color="#EFF9EF"
            icon="eye"
            isMobile={isMobile}
            testId="medical-info-box"
            title={medicalPlanTitle}
          />
          <InfoBox
            bullets={visionPlanCoversBullets}
            color="#F7FCFD"
            icon="eye_exam"
            isMobile={isMobile}
            testId="vision-info-box"
            title={visionPlanTitle}
          />
        </Layout.Group>
        <Link
          beforeIcon={{ icon: 'info', variant: 'outlined' }}
          data-auto-testid="medical-vs-vision-infographic-link"
          data-testid="medical-vs-vision-infographic-link"
          onClick={() => setIsDrawerOpen(true)}
          size={'$sm'}
        >
          {t('MEDICAL_SPECIALISTS_VS_VISION.Medical_specialists_vs_vision')}
        </Link>
      </Layout.Stack>
      <MedicalVisionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </React.Fragment>
  );
};
